import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { Add, Remove, VolumeOff, VolumeUp } from '@mui/icons-material';
import { sonosControl, setGroupVolume } from '../../services/sonosApi';
import { mergeSonosSpeakers } from '../../services/sonosState';
import { SONOS_BLUE } from './sonosUiStyles';

/** Paso de los botones − / + (unidades de volumen Sonos 0–100). */
const VOLUME_STEP = 2;

/** Área táctil cómoda (≥40 px) sin agrandar la barra: padding negativo visual vía margen. */
const NUDGE_BUTTON_SX = {
  color: '#ddd',
  width: 40,
  height: 40,
  p: 0,
  my: -0.75,
  flexShrink: 0,
  WebkitTapHighlightColor: 'transparent',
  '&:active': { opacity: 0.6 },
  '&.Mui-disabled': { color: '#555' },
} as const;

interface MinimalVolumeBarProps {
  speakerId: number;
  volume: number;
  mute?: boolean;
  disabled?: boolean;
  volumeTargetIds?: number[];
  onVolumeApplied?: (volume: number, mute: boolean) => void;
  showValue?: boolean;
  label?: string;
}

const MinimalVolumeBar: React.FC<MinimalVolumeBarProps> = ({
  speakerId,
  volume,
  mute = false,
  disabled,
  volumeTargetIds,
  onVolumeApplied,
  showValue = true,
  label,
}) => {
  const { t } = useTranslation();
  const [localVol, setLocalVol] = useState(volume ?? 0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const draggingRef = useRef(false);
  const ignorePropUntilRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (draggingRef.current || editing) return;
    if (Date.now() < ignorePropUntilRef.current) return;
    setLocalVol(volume ?? 0);
  }, [volume, editing]);

  useEffect(() => {
    if (!editing) return undefined;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [editing]);

  useEffect(() => {
    if (disabled) setEditing(false);
  }, [disabled]);

  const applyVolume = useCallback((v: number) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const targets = volumeTargetIds?.length ? volumeTargetIds : [speakerId];
        let updated: Awaited<ReturnType<typeof sonosControl>> | undefined;
        if (targets.length > 1) {
          await setGroupVolume(targets, v);
        } else {
          updated = await sonosControl(speakerId, 'volume', v);
        }
        ignorePropUntilRef.current = Date.now() + 10000;
        if (updated?.length) mergeSonosSpeakers(updated);
        onVolumeApplied?.(v, false);
      } catch { /* noop */ }
      finally {
        draggingRef.current = false;
      }
    }, 250);
  }, [speakerId, volumeTargetIds, onVolumeApplied]);

  const commitVolumeValue = useCallback((v: number) => {
    const clamped = Math.min(100, Math.max(0, Math.round(v)));
    ignorePropUntilRef.current = Date.now() + 10000;
    setLocalVol(clamped);
    onVolumeApplied?.(clamped, false);
    applyVolume(clamped);
  }, [applyVolume, onVolumeApplied]);

  const posFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return localVol;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return localVol;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(ratio * 100);
  }, [localVol]);

  const finishVolumeDrag = (clientX: number) => {
    if (!draggingRef.current) return;
    const v = posFromClientX(clientX);
    ignorePropUntilRef.current = Date.now() + 10000;
    setLocalVol(v);
    onVolumeApplied?.(v, false);
    applyVolume(v);
  };

  const toggleMute = async () => {
    try {
      const targets = volumeTargetIds?.length ? volumeTargetIds : [speakerId];
      const nextMute = !mute;
      const results = await Promise.all(targets.map((id) => sonosControl(id, 'mute', nextMute)));
      ignorePropUntilRef.current = Date.now() + 10000;
      const merged = results.flat();
      if (merged.length) mergeSonosSpeakers(merged);
      onVolumeApplied?.(localVol, nextMute);
    } catch { /* noop */ }
  };

  /** Botones − / +: paso fijo; los toques seguidos se agrupan en el debounce de applyVolume. */
  const nudgeVolume = (delta: number) => {
    if (disabled) return;
    commitVolumeValue(localVol + delta);
  };

  const beginEdit = () => {
    if (disabled) return;
    // Arranca vacío para tipear directo; el volumen actual queda como placeholder
    // y se conserva si se cancela (Escape) o se sale sin escribir.
    setDraft('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
  };

  const commitEdit = () => {
    if (!editing) return;
    setEditing(false);
    const raw = draft.replace(/[^\d]/g, '');
    setDraft('');
    if (raw === '') return;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n === Math.round(localVol)) return;
    commitVolumeValue(n);
  };

  return (
    <Box data-audio-no-swipe="" sx={{ width: '100%', minWidth: 0, mb: label ? 0.35 : 0 }}>
      {label && (
        <Typography sx={{ color: '#888', fontSize: 10, mb: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
        <IconButton
          size="small"
          onClick={toggleMute}
          disabled={disabled}
        sx={{ color: mute ? '#ff1744' : '#ddd', p: 0.35 }}
      >
        {mute ? <VolumeOff sx={{ fontSize: 22 }} /> : <VolumeUp sx={{ fontSize: 22 }} />}
        </IconButton>
        <IconButton
          aria-label={t('sonos.modal.volumeDown')}
          disabled={disabled || localVol <= 0}
          disableRipple
          onClick={(e) => { e.stopPropagation(); nudgeVolume(-VOLUME_STEP); }}
          sx={NUDGE_BUTTON_SX}
        >
          <Remove sx={{ fontSize: 22 }} />
        </IconButton>
        <Box
          ref={trackRef}
          onPointerDown={(e) => {
            if (disabled || editing) return;
            draggingRef.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
            e.preventDefault();
            e.stopPropagation();
            const v = posFromClientX(e.clientX);
            ignorePropUntilRef.current = Date.now() + 10000;
            setLocalVol(v);
            onVolumeApplied?.(v, false);
          }}
          onPointerMove={(e) => {
            if (!draggingRef.current || disabled) return;
            e.preventDefault();
            e.stopPropagation();
            setLocalVol(posFromClientX(e.clientX));
          }}
          onPointerUp={(e) => {
            if (!draggingRef.current) return;
            e.preventDefault();
            e.stopPropagation();
            finishVolumeDrag(e.clientX);
          }}
          onPointerCancel={(e) => {
            if (!draggingRef.current) return;
            finishVolumeDrag(e.clientX);
          }}
          sx={{
            flex: 1,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            touchAction: 'none',
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          <Box sx={{ position: 'relative', width: '100%', height: 3, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.22)' }}>
            <Box sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${localVol}%`,
              borderRadius: 2,
              backgroundColor: '#fff',
            }}
            />
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: `${localVol}%`,
              width: 11,
              height: 11,
              borderRadius: '50%',
              backgroundColor: '#fff',
              transform: 'translate(-50%, -50%)',
            }}
            />
          </Box>
        </Box>
        <IconButton
          aria-label={t('sonos.modal.volumeUp')}
          disabled={disabled || localVol >= 100}
          disableRipple
          onClick={(e) => { e.stopPropagation(); nudgeVolume(VOLUME_STEP); }}
          sx={NUDGE_BUTTON_SX}
        >
          <Add sx={{ fontSize: 22 }} />
        </IconButton>
        {showValue && (
          editing ? (
            <TextField
              inputRef={inputRef}
              value={draft}
              placeholder={String(Math.round(localVol))}
              onChange={(e) => {
                const v = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
                setDraft(v);
              }}
              onBlur={() => { commitEdit(); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              onClick={(e) => e.stopPropagation()}
              variant="standard"
              inputProps={{
                inputMode: 'numeric',
                'aria-label': t('sonos.modal.volumeLabel'),
                style: {
                  textAlign: 'right',
                  fontSize: 15,
                  fontWeight: 700,
                  color: SONOS_BLUE,
                  padding: 0,
                  width: 28,
                },
              }}
              sx={{
                width: 32,
                flexShrink: 0,
                // Casi imperceptible: indica que el campo ya está vacío y se escribe directo.
                '& input::placeholder': { color: 'rgba(255,255,255,0.18)', opacity: 1 },
                '& .MuiInput-underline:before': { borderBottomColor: `${SONOS_BLUE}66` },
                '& .MuiInput-underline:after': { borderBottomColor: SONOS_BLUE },
              }}
            />
          ) : (
            <Typography
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-label={t('sonos.modal.editVolume')}
              onClick={(e) => {
                e.stopPropagation();
                beginEdit();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  beginEdit();
                }
              }}
              sx={{
                color: '#ddd',
                fontSize: 15,
                fontWeight: 700,
                minWidth: 28,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
                cursor: disabled ? 'default' : 'pointer',
                WebkitTapHighlightColor: 'transparent',
                lineHeight: 1.1,
                flexShrink: 0,
                '&:active': disabled ? undefined : { opacity: 0.75 },
              }}
            >
              {localVol}
            </Typography>
          )
        )}
      </Box>
    </Box>
  );
};

export default MinimalVolumeBar;
